import type { PointerInteraction } from '../interaction/pointer';

// Demo-view click feedback. Clicking a hero in the experience flashes a small
// label at the cursor — "generator on" for the generator, the hero's friendly
// name otherwise — that rises and fades. This replaces the persistent selection
// outline (HoverHighlight runs with select=false in demo), which read as a stuck
// UI artifact in a diegetic scene. Authoring views keep the outline (the
// inspector needs a durable "active hero"), so this module isn't created there.
//
// Fed by the same pointer 'click' the InteractionEngine uses, with the click's
// viewport coords riding along on the event (PointerEventInfo.clientX/Y) — so
// there's no second DOM listener and no listener-order race for the position.

const HOLD_MS = 900; // fully visible before it starts to fade
const FADE_MS = 520; // fade-out duration

export interface DemoChipDeps {
  pointer: PointerInteraction;
  /** Override the chip text per hero. Default: hero_generator → "generator on",
   *  everything else → the de-prefixed, space-separated id. Return '' to suppress
   *  the chip for a given hero. */
  labelFor?: (heroId: string) => string;
}

export interface DemoChip {
  dispose(): void;
}

function defaultLabel(heroId: string): string {
  if (heroId === 'hero_generator') return 'generator on';
  return heroId.replace(/^hero_/, '').replace(/_/g, ' ');
}

export function createDemoChip(deps: DemoChipDeps): DemoChip {
  const label = deps.labelFor ?? defaultLabel;
  const unsub = deps.pointer.on('click', (info) => {
    if (!info.heroId) return;
    const text = label(info.heroId);
    if (!text) return;
    spawnChip(
      text,
      info.clientX ?? window.innerWidth / 2,
      info.clientY ?? window.innerHeight / 2,
    );
  });
  return { dispose: () => unsub() };
}

function spawnChip(text: string, x: number, y: number): void {
  const chip = document.createElement('div');
  chip.textContent = text;
  chip.style.cssText = [
    'position: fixed',
    `left: ${Math.round(x)}px`,
    `top: ${Math.round(y - 14)}px`,
    'transform: translate(-50%, -100%)',
    'padding: 4px 9px',
    'background: rgba(20, 26, 38, 0.62)',
    'border: 1px solid rgba(150, 178, 228, 0.40)',
    'border-radius: 999px',
    'color: #eaf1ff',
    'font: 600 11px/1 system-ui, sans-serif',
    'letter-spacing: 0.04em',
    'white-space: nowrap',
    'pointer-events: none',
    'z-index: 1200',
    'backdrop-filter: blur(4px)',
    '-webkit-backdrop-filter: blur(4px)',
    'box-shadow: 0 2px 10px rgba(0, 0, 0, 0.30)',
    'opacity: 0',
    'transition: opacity 160ms ease, transform 160ms ease',
  ].join('; ');
  document.body.appendChild(chip);

  // Fade in + lift on the next frame, hold, then fade out drifting upward.
  requestAnimationFrame(() => {
    chip.style.opacity = '1';
    chip.style.transform = 'translate(-50%, -100%) translateY(-6px)';
  });
  window.setTimeout(() => {
    chip.style.transition = `opacity ${FADE_MS}ms ease, transform ${FADE_MS}ms ease`;
    chip.style.opacity = '0';
    chip.style.transform = 'translate(-50%, -100%) translateY(-16px)';
  }, HOLD_MS);
  window.setTimeout(() => chip.remove(), HOLD_MS + FADE_MS + 60);
}
